import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import React, { useState, useEffect } from 'react'
import { 
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
  Activity,
  Calendar,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Globe,
  Zap,
  Database,
  Clock,
  Target,
  Filter
} from 'lucide-react'

interface AnalyticsData {
  revenue: {
    total: number
    monthly: number
    growth: number
    trend: number[]
  }
  operators: {
    total: number
    active: number
    growth: number
    signups: number[]
  }
  bookings: {
    total: number
    monthly: number
    growth: number
    trend: number[]
  }
  performance: {
    apiCalls: { value: number; estimate: boolean } | number
    responseTime: number
    uptime: number
    errorRate: number
  }
}

interface TopOperator {
  id: string
  name: string
  revenue: number
  bookings: number
  growth: number
}

const ADMIN_BRAND = {
  primary: '#4F46E5',
  primaryLight: '#EEF2FF',
  primaryText: '#3730A3',
  gradient: 'linear-gradient(to right, #2563EB, #4F46E5)',
}

const getApiCallsValue = (apiCalls: { value: number; estimate: boolean } | number): number =>
  typeof apiCalls === 'object' ? apiCalls.value : apiCalls

const isApiCallsEstimate = (apiCalls: { value: number; estimate: boolean } | number): boolean =>
  typeof apiCalls === 'object' ? apiCalls.estimate : false

const PlatformAnalytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d')
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    revenue: {
      total: 0,
      monthly: 0,
      growth: 0,
      trend: []
    },
    operators: {
      total: 0,
      active: 0,
      growth: 0,
      signups: []
    },
    bookings: {
      total: 0,
      monthly: 0,
      growth: 0,
      trend: []
    },
    performance: {
      apiCalls: { value: 0, estimate: true },
      responseTime: 0,
      uptime: 0,
      errorRate: 0
    }
  })

  const [topOperators, setTopOperators] = useState<TopOperator[]>([])
  const [loading, setLoading] = useState(true)
  const [distributionData, setDistributionData] = useState({
    bookingDistribution: [],
    geographicDistribution: []
  })

  // Fetch analytics data from API
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true)
        
        // Fetch main analytics data
        const analyticsResponse = await authFetch(`${API_BASE_URL}/api/super-admin/analytics?time_range=${timeRange}`)
        if (analyticsResponse.ok) {
          const data = await analyticsResponse.json()
          if (data.success) {
            setAnalytics(data.analytics)
            setTopOperators(data.topOperators || [])
          }
        }

        // Fetch distribution data
        const distributionResponse = await authFetch(`${API_BASE_URL}/api/super-admin/analytics/distribution`)
        if (distributionResponse.ok) {
          const distData = await distributionResponse.json()
          if (distData.success) {
            setDistributionData({
              bookingDistribution: distData.bookingDistribution,
              geographicDistribution: distData.geographicDistribution
            })
          }
        }

      } catch (error) {
        console.error('Error fetching analytics:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAnalytics()
  }, [timeRange])

  const getTimeRangeLabel = (range: string) => {
    switch (range) {
      case '7d': return 'Last 7 days'
      case '30d': return 'Last 30 days'
      case '90d': return 'Last 90 days'
      case '1y': return 'Last year'
      default: return 'Last 30 days'
    }
  }

  const MetricCard: React.FC<{
    title: string
    value: string | number
    change: number
    trend?: number[]
    icon: React.ElementType
    color: string
    brandColor?: { bg: string; text: string; bar: string }
    extra?: React.ReactNode
  }> = ({ title, value, change, trend, icon: Icon, color, brandColor, extra }) => (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <div className="flex items-center justify-between">
        <div className={brandColor ? 'p-3 rounded-lg' : `p-3 rounded-lg bg-${color}-100`} style={brandColor ? { backgroundColor: brandColor.bg } : undefined}>
          <Icon className={brandColor ? 'h-6 w-6' : `h-6 w-6 text-${color}-600`} style={brandColor ? { color: brandColor.text } : undefined} />
        </div>
        <div className={`flex items-center text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {change >= 0 ? <ArrowUpRight className="h-4 w-4 mr-1" /> : <ArrowDownRight className="h-4 w-4 mr-1" />}
          {Math.abs(change).toFixed(1)}%
        </div>
      </div>
      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <div className="flex items-center">
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {extra}
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-end space-x-1 h-8">
          {trend.map((point, index) => (
            <div
              key={index}
              className={brandColor ? 'rounded-sm flex-1' : `bg-${color}-500 rounded-sm flex-1`}
              style={{ height: `${(point / Math.max(...trend)) * 100}%`, ...(brandColor ? { backgroundColor: brandColor.bar } : {}) }}
            ></div>
          ))}
        </div>
      )}
    </div>
  )

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl p-6 text-white" style={{ background: ADMIN_BRAND.gradient }}>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mr-3"></div>
            <span className="text-lg">Loading analytics data...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="h-6 w-6 text-blue-600 mr-2" />
            Platform Analytics
          </h1>
          <p className="text-gray-600 mt-1">Revenue, usage, and performance insights across the platform</p>
        </div>
        <div className="flex space-x-3">
          <select
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <button 
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Revenue"
          value={`$${analytics.revenue.total.toLocaleString()}`}
          change={analytics.revenue.growth}
          trend={analytics.revenue.trend}
          icon={DollarSign}
          color="green"
        />
        <MetricCard
          title="Active Operators"
          value={analytics.operators.active}
          change={analytics.operators.growth}
          trend={analytics.operators.signups}
          icon={Users}
          color="blue"
        />
        <MetricCard
          title="Total Bookings"
          value={analytics.bookings.total.toLocaleString()}
          change={analytics.bookings.growth}
          trend={analytics.bookings.trend}
          icon={Calendar}
          color=""
          brandColor={{ bg: ADMIN_BRAND.primaryLight, text: ADMIN_BRAND.primary, bar: ADMIN_BRAND.primary }}
        />
        <MetricCard
          title="API Calls"
          value={getApiCallsValue(analytics.performance.apiCalls).toLocaleString()}
          change={8.2}
          icon={Activity}
          color="orange"
          extra={isApiCallsEstimate(analytics.performance.apiCalls) ? (
            <span className="ml-1 text-xs font-normal text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded mt-1">Estimate</span>
          ) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Revenue Trends</h3>
              <div className="text-sm text-gray-500">{getTimeRangeLabel(timeRange)}</div>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-2xl font-bold text-gray-900">${analytics.revenue.monthly.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">This month</div>
                </div>
                <div className={`flex items-center text-sm ${analytics.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analytics.revenue.growth >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <ArrowDownRight className="h-4 w-4 mr-1" />}
                  {analytics.revenue.growth.toFixed(1)}% from last month
                </div>
              </div>
              
              {/* Simple chart representation */}
              <div className="h-32 flex items-end space-x-2">
                {analytics.revenue.trend.map((value, index) => (
                  <div key={index} className="flex-1 bg-green-100 rounded-t-md relative">
                    <div 
                      className="bg-green-500 rounded-t-md transition-all duration-500"
                      style={{ height: `${(value / Math.max(...analytics.revenue.trend)) * 100}%` }}
                    ></div>
                    <div className="absolute bottom-0 left-0 right-0 text-center text-xs text-gray-500 -mb-5">
                      Week {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Top Operators */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Top Performing Operators</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {topOperators.map((operator, index) => (
                <div key={operator.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{operator.name}</div>
                      <div className="text-sm text-gray-500">{operator.bookings} bookings</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-900">${operator.revenue.toLocaleString()}</div>
                    <div className={`text-sm flex items-center ${operator.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {operator.growth >= 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                      {Math.abs(operator.growth).toFixed(1)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Platform Performance */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Platform Performance</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="h-8 w-8 text-blue-600" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{analytics.performance.responseTime}ms</div>
              <div className="text-sm text-gray-500">Avg Response Time</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Activity className="h-8 w-8 text-green-600" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{analytics.performance.uptime}%</div>
              <div className="text-sm text-gray-500">Uptime</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: ADMIN_BRAND.primaryLight }}>
                <Database className="h-8 w-8" style={{ color: ADMIN_BRAND.primary }} />
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {getApiCallsValue(analytics.performance.apiCalls).toLocaleString()}
                {isApiCallsEstimate(analytics.performance.apiCalls) && (
                  <span className="ml-1 text-xs font-normal text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Estimate</span>
                )}
              </div>
              <div className="text-sm text-gray-500">API Calls ({timeRange})</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Target className="h-8 w-8 text-red-600" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{analytics.performance.errorRate}%</div>
              <div className="text-sm text-gray-500">Error Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Patterns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Booking Distribution</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {distributionData.bookingDistribution.map((item: any) => (
                <div key={item.category} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full bg-${item.color}-500`}></div>
                    <span className="text-sm font-medium text-gray-700">{item.category}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full bg-${item.color}-500`}
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-500 w-8">{item.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Geographic Distribution</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {distributionData.geographicDistribution.map((item: any) => (
                <div key={item.region} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-700">{item.region}</div>
                      <div className="text-xs text-gray-500">{item.bookings} bookings</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">{item.percentage}%</div>
                    <div className="w-16 bg-gray-200 rounded-full h-1.5 mt-1">
                      <div 
                        className="h-1.5 rounded-full bg-blue-500"
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlatformAnalytics